/**
 * Phase 8 · C-21 — Sequence recipients service.
 *
 * Reads / writes `ggj_b2b_sequence_recipients` (migration 100). The table has
 * no `org_id` column; row-level security is enforced via the parent sequence
 * (`ggj_b2b_email_sequences.org_id = current_org_id()`), so callers pass
 * `sequenceId` instead of `orgId`.
 *
 * Powers:
 *   - Recipients tab in SequencePage (list + unenroll).
 *   - Smart Match → "Create sequence" enrolment handoff (enroll).
 */
import { supabase } from '../client';

export type SequenceRecipientStatus =
  | 'active'
  | 'paused'
  | 'completed'
  | 'unsubscribed';

export interface SequenceRecipient {
  id: string;
  sequenceId: string;
  candidateId: string;
  candidateName: string;
  candidateEmail: string | null;
  enrolledAt: string;
  unenrolledAt: string | null;
  status: SequenceRecipientStatus;
}

interface RecipientRow {
  id: string;
  sequence_id: string;
  candidate_id: string;
  enrolled_at: string;
  unenrolled_at: string | null;
  status: string | null;
  candidate:
    | { full_name: string | null; email: string | null }
    | { full_name: string | null; email: string | null }[]
    | null;
}

function normaliseStatus(s: string | null | undefined): SequenceRecipientStatus {
  if (s === 'active' || s === 'paused' || s === 'completed' || s === 'unsubscribed') return s;
  return 'active';
}

function pickCandidate(c: RecipientRow['candidate']): { full_name: string | null; email: string | null } | null {
  if (!c) return null;
  return Array.isArray(c) ? c[0] ?? null : c;
}

function mapRow(r: RecipientRow): SequenceRecipient {
  const cand = pickCandidate(r.candidate);
  return {
    id: r.id,
    sequenceId: r.sequence_id,
    candidateId: r.candidate_id,
    candidateName: cand?.full_name?.trim() || 'Unnamed candidate',
    candidateEmail: cand?.email ?? null,
    enrolledAt: r.enrolled_at,
    unenrolledAt: r.unenrolled_at,
    status: normaliseStatus(r.status),
  };
}

export const sequenceRecipientsService = {
  /**
   * List all recipients for a sequence, newest enrolments first. Joins the
   * candidate row for display (name + email). Throws on error so TanStack
   * Query can surface `isError`.
   */
  async list(sequenceId: string): Promise<SequenceRecipient[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_sequence_recipients')
      .select(
        'id, sequence_id, candidate_id, enrolled_at, unenrolled_at, status, candidate:ggj_b2b_candidates(full_name, email)',
      )
      .eq('sequence_id', sequenceId)
      .order('enrolled_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => mapRow(r as RecipientRow));
  },

  /**
   * Enrol one or more candidates into a sequence. De-duplicated on
   * (sequence_id, candidate_id) by the UNIQUE constraint — we `upsert` with
   * `ignoreDuplicates` so repeat calls are idempotent (important for the
   * stash-consumption path that may fire more than once on remount).
   *
   * No-op when `candidateIds` is empty.
   */
  async enroll(sequenceId: string, candidateIds: string[]): Promise<void> {
    const unique = Array.from(new Set(candidateIds.filter(Boolean)));
    if (unique.length === 0) return;

    const rows = unique.map((candidateId) => ({
      sequence_id: sequenceId,
      candidate_id: candidateId,
      status: 'active' as const,
    }));

    const { error } = await supabase
      .from('ggj_b2b_sequence_recipients')
      .upsert(rows, { onConflict: 'sequence_id,candidate_id', ignoreDuplicates: true });

    if (error) throw new Error(error.message);
  },

  /**
   * Unenrol a recipient. Soft-style: stamp `unenrolled_at`, flip status to
   * 'unsubscribed' so audit rows survive, and the Recipients tab filters
   * out unsubscribed rows from the default view (callers can opt in).
   */
  async unenroll(sequenceId: string, recipientId: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_sequence_recipients')
      .update({
        status: 'unsubscribed',
        unenrolled_at: new Date().toISOString(),
      })
      .eq('id', recipientId)
      .eq('sequence_id', sequenceId);

    if (error) throw new Error(error.message);
  },
};
